// The welcome-page MODEL: everything that is true about a welcome page before
// anything is drawn.
//
// js/ui/welcome-editor.js is a free-canvas design surface — drag, resize, snap,
// an inspector, an image picker, DOMPurify — and buried inside all of that DOM
// code sat the model the rest of the app depends on: the v2 page shape, the
// migration that keeps pages authored in the old block-list editor working, and
// the plain-text summary that ends up on public tournament cards. js/app.js and
// js/ui/tournament-form.js import those three from the editor module purely to
// reach this logic, and none of it could be tested, because loading the editor
// needs a browser.
//
// So the model lives here, with no DOM anywhere in it:
//
//   { version: 2,
//     canvas: { width, height, bg, bgImageUrl },
//     items:  [ { id, type, x, y, w, h, props } ] }
//
// Deliberately NOT here (see welcome-editor.js): sanitising (DOMPurify), building
// elements, selection, the toolbar, the inspector, gesture wiring. What IS here
// from the gesture side is only the arithmetic those gestures do — resizeBox,
// snapTargets, applySnap — which is pure geometry the drag handlers feed pointer
// deltas into.
//
// The one thing that had to be rewritten rather than moved is text extraction.
// The editor read plain text out of rich text with `new DOMParser()`, which needs
// a browser; htmlToPlainText() below does the same job with string operations, so
// the model can produce a summary under Node. It is never used to produce HTML —
// only text — so it is not, and must not become, a sanitizer.

// ----- geometry limits -----

export const PAGE_WIDTHS = [700, 900, 1100];
export const DEFAULT_WIDTH = 900;
export const DEFAULT_HEIGHT = 720;
export const MIN_HEIGHT = 240;
export const MAX_HEIGHT = 6000;
export const MIN_ITEM_W = 32;
export const MIN_ITEM_H = 20;
export const GRID = 10;          // grid step, in design pixels

/** Number(v) when that is finite, otherwise the fallback. */
export function num(v, fallback) {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
}

export function clamp(v, lo, hi) {
    return Math.min(hi, Math.max(lo, v));
}

/** Item id: time-ordered enough to read, random enough not to collide. */
export function uid() {
    return 'b' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

// ----- item types -----

// Palette entries double as the default size for a freshly placed item.
export const ITEM_TYPES = [
    { type: 'banner', label: 'Banner', icon: 'ri-image-2-line', w: 900, h: 240 },
    { type: 'heading', label: 'Heading', icon: 'ri-heading', w: 520, h: 64 },
    { type: 'text', label: 'Text', icon: 'ri-text', w: 440, h: 180 },
    { type: 'image', label: 'Image', icon: 'ri-image-line', w: 340, h: 240 },
    { type: 'maps', label: 'Map', icon: 'ri-map-pin-2-line', w: 440, h: 300 },
    { type: 'button', label: 'Button', icon: 'ri-cursor-line', w: 280, h: 56 },
    { type: 'box', label: 'Box', icon: 'ri-checkbox-blank-line', w: 340, h: 220 },
    { type: 'divider', label: 'Divider', icon: 'ri-separator', w: 440, h: 24 }
];

/** The palette entry for a type, or null for anything unknown. */
export function typeMeta(type) {
    return ITEM_TYPES.find(t => t.type === type) || null;
}

/** A fresh props object for a type — always a new object, safe to mutate. */
export function defaultProps(type) {
    switch (type) {
        case 'banner':
            // Empty heading/subheading start as gray placeholders in the editor.
            return { heading: '', subheading: '', bgColor: '#1e293b', textColor: '#ffffff', bgImageUrl: '', leftImageUrl: '', rightImageUrl: '', align: 'center', radius: 16 };
        case 'heading':
            return { text: '', level: 'h2', color: '', align: 'left', valign: 'middle' };
        case 'text':
            return { html: '', color: '', align: 'left', valign: 'top', fontSize: 17 };
        case 'image':
            return { url: '', alt: '', fit: 'cover', rounded: true };
        case 'maps':
            return { address: '', zoom: 14 };
        case 'button':
            return { label: 'Register for this tournament', action: 'register', url: '', bgColor: '#3b82f6', align: 'center', valign: 'middle' };
        case 'box':
            return { bgColor: '#f1f5f9', borderColor: '', radius: 14 };
        case 'divider':
            return { color: '', thickness: 1 };
        default:
            return {};
    }
}

export function makeItem(type, x, y) {
    const meta = typeMeta(type) || { w: 300, h: 120 };
    return { id: uid(), type, x: Math.round(x || 0), y: Math.round(y || 0), w: meta.w, h: meta.h, props: defaultProps(type) };
}

export function emptyPage(width) {
    return { version: 2, canvas: { width: width || DEFAULT_WIDTH, height: DEFAULT_HEIGHT, bg: '', bgImageUrl: '' }, items: [] };
}

// ----- normalisation -----

function normalizeCanvas(c) {
    const src = c || {};
    return {
        width: clamp(Math.round(num(src.width, DEFAULT_WIDTH)), 320, 1600),
        height: clamp(Math.round(num(src.height, DEFAULT_HEIGHT)), MIN_HEIGHT, MAX_HEIGHT),
        bg: typeof src.bg === 'string' ? src.bg : '',
        bgImageUrl: typeof src.bgImageUrl === 'string' ? src.bgImageUrl : ''
    };
}

function normalizeItem(it) {
    if (!it || !it.type || !typeMeta(it.type)) return null;
    const meta = typeMeta(it.type);
    return {
        id: it.id || uid(),
        type: it.type,
        x: Math.round(num(it.x, 0)),
        y: Math.round(num(it.y, 0)),
        w: Math.max(MIN_ITEM_W, Math.round(num(it.w, meta.w))),
        h: Math.max(MIN_ITEM_H, Math.round(num(it.h, meta.h))),
        props: Object.assign(defaultProps(it.type), it.props || {})
    };
}

// Accepts either format and always returns the v2 shape. Exported because the
// app stages welcome pages fetched from Firestore (which may still be v1) into
// state before the editor ever opens.
export function normalizeWelcomePage(page) {
    if (!page || typeof page !== 'object') return emptyPage();
    if (Array.isArray(page.items)) {
        return {
            version: 2,
            canvas: normalizeCanvas(page.canvas),
            items: page.items.map(normalizeItem).filter(Boolean)
        };
    }
    if (Array.isArray(page.blocks)) return migrateBlocks(page.blocks);
    return emptyPage();
}

/** A detached copy — what the editor hands back on save. */
export function cloneWelcomePage(page) {
    return JSON.parse(JSON.stringify(page));
}

// ----- v1 (block list) -> v2 (canvas) migration -----

// Rough rendered height of a legacy block at the given width, used only to lay
// migrated pages out sensibly; the arbiter can drag anything afterwards.
function estimateHeight(block, width) {
    const p = (block && block.props) || {};
    switch (block.type) {
        case 'banner':
            return (p.leftImageUrl || p.rightImageUrl) ? 260 : 220;
        case 'heading': {
            const perLine = { h1: 2.1, h2: 1.7, h3: 1.4, h4: 1.2 }[p.level || 'h2'] || 1.7;
            const lines = Math.max(1, Math.ceil(String(p.text || '').length / Math.max(12, width / (perLine * 11))));
            return Math.round(lines * perLine * 26) + 12;
        }
        case 'text': {
            const text = htmlToPlainText(p.html || '');
            const breaks = (String(p.html || '').match(/<(br|\/p|\/li|\/h[1-4])\b/gi) || []).length;
            const lines = Math.max(1, Math.ceil(text.length / Math.max(20, width / 8.6))) + breaks;
            return Math.max(56, Math.round(lines * 28) + 12);
        }
        case 'image':
            return Math.round(width * clamp(num(p.width, 100), 10, 100) / 100 * 0.66);
        case 'maps':
            return clamp(Math.round(num(p.height, 300)), 150, 600);
        case 'button':
            return 60;
        case 'divider':
            return 24;
        case 'spacer':
            return clamp(Math.round(num(p.height, 24)), 8, 200);
        default:
            return 120;
    }
}

function migrateOne(block, x, y, w) {
    const item = normalizeItem({
        id: block.id,
        type: block.type,
        x, y, w,
        h: estimateHeight(block, w),
        props: block.props || {}
    });
    if (!item) return null;
    if (item.type === 'image') {
        // v1 images were sized by a width percentage of the column; fold that into
        // the box width and never crop what the arbiter already published.
        const pct = clamp(num((block.props || {}).width, 100), 10, 100);
        item.w = Math.max(MIN_ITEM_W, Math.round(w * pct / 100));
        item.props.fit = 'contain';
        if ((block.props || {}).align === 'center') item.x = Math.round(x + (w - item.w) / 2);
        else if ((block.props || {}).align === 'right') item.x = Math.round(x + w - item.w);
    }
    return item;
}

function migrateBlocks(blocks) {
    const page = emptyPage(DEFAULT_WIDTH);
    const W = page.canvas.width;
    let y = 0;
    (blocks || []).forEach(block => {
        if (!block || !block.type) return;
        if (block.type === 'spacer') { y += estimateHeight(block, W); return; }
        if (block.type === 'columns') {
            const cols = Array.isArray(block.columns) ? block.columns : [];
            if (!cols.length) return;
            const gap = clamp(num((block.props || {}).gap, 24), 0, 64);
            const colW = Math.floor((W - gap * (cols.length - 1)) / cols.length);
            let tallest = 0;
            cols.forEach((col, ci) => {
                const cx = ci * (colW + gap);
                let cy = y;
                (col || []).forEach(child => {
                    const item = migrateOne(child, cx, cy, colW);
                    if (!item) return;
                    page.items.push(item);
                    cy += item.h + 14;
                });
                tallest = Math.max(tallest, cy - y);
            });
            y += tallest + 10;
            return;
        }
        const item = migrateOne(block, 0, y, W);
        if (!item) return;
        page.items.push(item);
        y += item.h + 20;
    });
    page.canvas.height = clamp(Math.round(y + 40), MIN_HEIGHT, MAX_HEIGHT);
    return page;
}

// ----- text extraction -----

// The handful of named entities a contenteditable actually produces. Anything
// else is left as written, which is the honest outcome for a plain-text excerpt.
const NAMED_ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: '\u00a0' };

/**
 * The visible text of an HTML fragment, whitespace collapsed — the DOM-free
 * equivalent of `new DOMParser().parseFromString(html).body.textContent`.
 * Tags are dropped (not replaced by a space, matching textContent), then
 * character references are decoded in a single pass so that a literal "&amp;lt;"
 * comes back as "&lt;" rather than being decoded twice.
 *
 * Output is text, never HTML. Do not use it as a sanitizer.
 */
export function htmlToPlainText(html) {
    if (html == null) return '';
    return String(html)
        .replace(/<[^>]*>/g, '')
        .replace(/&(#\d+|#[xX][0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g, (match, ref) => {
            if (ref[0] === '#') {
                const code = ref[1] === 'x' || ref[1] === 'X'
                    ? parseInt(ref.slice(2), 16)
                    : parseInt(ref.slice(1), 10);
                if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff) return match;
                return String.fromCodePoint(code);
            }
            const named = NAMED_ENTITIES[ref.toLowerCase()];
            return named === undefined ? match : named;
        })
        .replace(/\s+/g, ' ')
        .trim();
}

// ----- reading a page -----

export function welcomePageIsEmpty(page) {
    if (!page || typeof page !== 'object') return true;
    if (Array.isArray(page.items)) return page.items.length === 0;
    if (Array.isArray(page.blocks)) return page.blocks.length === 0;
    return true;
}

// A short plain-text excerpt used for the public tournament cards / welcomeMessage.
// Items are read in visual order (top to bottom, then left to right).
export function welcomePageToSummary(page) {
    if (welcomePageIsEmpty(page)) return '';
    const p = normalizeWelcomePage(page);
    const ordered = p.items.slice().sort((a, b) => (a.y - b.y) || (a.x - b.x));
    for (const it of ordered) {
        const props = it.props || {};
        if (it.type === 'text' && props.html) {
            const t = htmlToPlainText(props.html);
            if (t) return t.slice(0, 200);
        }
        if (it.type === 'heading' && props.text) return String(props.text).slice(0, 200);
        if (it.type === 'banner' && (props.subheading || props.heading)) {
            return String(props.subheading || props.heading).slice(0, 200);
        }
    }
    return '';
}

// ----- style mapping (plain value in, plain string out) -----

export function alignToStyle(align) {
    return align === 'center' ? 'center' : align === 'right' ? 'right' : 'left';
}

export function valignToFlex(valign) {
    return valign === 'middle' ? 'center' : valign === 'bottom' ? 'flex-end' : 'flex-start';
}

// Escape a URL for safe embedding inside a CSS url("...") value. NOTE: do NOT
// run download URLs through encodeURI() — Firebase Storage URLs are already
// percent-encoded (e.g. %2F), and encodeURI re-encodes the '%' to '%25', which
// breaks the link so the image never loads. We only need to neutralize the few
// characters that could terminate the quoted string.
export function cssUrlValue(url) {
    return String(url || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/[\r\n]/g, '');
}

// ----- item operations (all return new values; nothing is mutated in place) -----

/** The canvas height needed to keep `item` on the surface, given the current one. */
export function grownCanvasHeight(height, item) {
    const need = item.y + item.h + 40;
    if (need <= height) return height;
    return clamp(Math.ceil(need / GRID) * GRID, MIN_HEIGHT, MAX_HEIGHT);
}

/** The height the surface may not shrink below without clipping an item. */
export function canvasHeightFloor(items) {
    return (items || []).reduce((m, i) => Math.max(m, i.y + i.h + 20), MIN_HEIGHT);
}

/**
 * A new item of `type`, parked in the first clear spot below what is already
 * there — centred, except banners, which run full bleed from the left edge.
 * Returns null for an unknown type.
 */
export function placeNewItem(canvas, items, type) {
    const meta = typeMeta(type);
    if (!meta) return null;
    const list = items || [];
    const bottom = list.reduce((m, i) => Math.max(m, i.y + i.h), 0);
    const w = Math.min(meta.w, canvas.width);
    const x = type === 'banner' ? 0 : Math.round((canvas.width - w) / 2);
    const y = list.length ? bottom + 24 : 0;
    const item = makeItem(type, x, y);
    item.w = w;
    return item;
}

/** A deep copy of `item` with a fresh id, nudged 20px down-right but kept on canvas. */
export function duplicateItemOnCanvas(canvas, item) {
    const copy = JSON.parse(JSON.stringify(item));
    copy.id = uid();
    copy.x = Math.min(item.x + 20, Math.max(0, canvas.width - item.w));
    copy.y = item.y + 20;
    return copy;
}

/** `items` without the one carrying `id` (the same array back if there is none). */
export function removeItem(items, id) {
    const list = items || [];
    const i = list.findIndex(it => it.id === id);
    if (i < 0) return list;
    return list.slice(0, i).concat(list.slice(i + 1));
}

// Paint order == array order, so "bring to front" is a move to the end.
// `where` is 'front' or 'back'; the same array comes back if `id` is not there.
export function reorderItems(items, id, where) {
    const list = items || [];
    const i = list.findIndex(it => it.id === id);
    if (i < 0) return list;
    const rest = list.slice(0, i).concat(list.slice(i + 1));
    return where === 'front' ? rest.concat([list[i]]) : [list[i]].concat(rest);
}

// ----- gesture arithmetic (fed pointer deltas by the editor's drag handlers) -----

/**
 * The box a resize handle drags out. `dir` is a compass direction ('nw', 'e', …);
 * pulling an edge past the minimum size pins that edge instead of inverting the box.
 */
export function resizeBox(o, dir, dx, dy) {
    let { x, y, w, h } = o;
    if (dir.includes('e')) w = o.w + dx;
    if (dir.includes('s')) h = o.h + dy;
    if (dir.includes('w')) { w = o.w - dx; x = o.x + dx; }
    if (dir.includes('n')) { h = o.h - dy; y = o.y + dy; }
    if (w < MIN_ITEM_W) { if (dir.includes('w')) x = o.x + o.w - MIN_ITEM_W; w = MIN_ITEM_W; }
    if (h < MIN_ITEM_H) { if (dir.includes('n')) y = o.y + o.h - MIN_ITEM_H; h = MIN_ITEM_H; }
    return { x, y, w, h };
}

// Edges/centres worth snapping to: every other element, plus the surface itself.
export function snapTargets(canvas, items, skipId) {
    const v = [0, canvas.width / 2, canvas.width];
    const h = [0, canvas.height];
    (items || []).forEach(o => {
        if (o.id === skipId) return;
        v.push(o.x, o.x + o.w / 2, o.x + o.w);
        h.push(o.y, o.y + o.h / 2, o.y + o.h);
    });
    return { v, h };
}

/**
 * Nudge `box` (mutated in place) onto the nearest guide within `tol`; returns the
 * guide lines that actually caught, for the on-canvas indicators. A move snaps on
 * left/centre/right and top/middle/bottom; a resize only on the edges, since its
 * centre follows from them.
 */
export function applySnap(box, targets, tol, isMove) {
    const guides = [];

    const near = (value, list) => {
        let best = null;
        list.forEach(t => {
            const d = Math.abs(t - value);
            if (d <= tol && (!best || d < best.d)) best = { t, d };
        });
        return best;
    };

    // Vertical alignment (x axis): left / centre / right edges.
    const vCands = isMove
        ? [{ value: box.x, off: 0 }, { value: box.x + box.w / 2, off: box.w / 2 }, { value: box.x + box.w, off: box.w }]
        : [{ value: box.x, off: 0 }, { value: box.x + box.w, off: box.w }];
    let bestV = null;
    vCands.forEach(cand => {
        const hit = near(cand.value, targets.v);
        if (hit && (!bestV || hit.d < bestV.d)) bestV = { t: hit.t, d: hit.d, off: cand.off, edge: cand.off === 0 ? 'left' : 'right' };
    });
    if (bestV) {
        if (isMove) box.x = bestV.t - bestV.off;
        else if (bestV.edge === 'left') { box.w = box.x + box.w - bestV.t; box.x = bestV.t; }
        else box.w = bestV.t - box.x;
        guides.push({ axis: 'v', at: bestV.t });
    }

    const hCands = isMove
        ? [{ value: box.y, off: 0 }, { value: box.y + box.h / 2, off: box.h / 2 }, { value: box.y + box.h, off: box.h }]
        : [{ value: box.y, off: 0 }, { value: box.y + box.h, off: box.h }];
    let bestH = null;
    hCands.forEach(cand => {
        const hit = near(cand.value, targets.h);
        if (hit && (!bestH || hit.d < bestH.d)) bestH = { t: hit.t, d: hit.d, off: cand.off, edge: cand.off === 0 ? 'top' : 'bottom' };
    });
    if (bestH) {
        if (isMove) box.y = bestH.t - bestH.off;
        else if (bestH.edge === 'top') { box.h = box.y + box.h - bestH.t; box.y = bestH.t; }
        else box.h = bestH.t - box.y;
        guides.push({ axis: 'h', at: bestH.t });
    }
    return guides;
}
